import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "booking-sidebar" }

import { onBeforeMount, ref, inject, shallowRef, withDefaults, computed } from "vue";
import { useStore } from "vuex";

/**
 * VueUseユーティリティライブラリ
 * - Fn: 関数の型定義
 * - get: リアクティブな値の取得
 * - set: リアクティブな値の設定
 * - useArrayFindIndex: 配列内の要素のインデックスを検索
 * - useSorted: 配列のソート
 * - useDateFormat: 日付のフォーマット
 */
import { Fn, get, set, useArrayFindIndex, useSorted, useDateFormat } from "@vueuse/core";

/**
 * PrimeVue UIコンポーネント
 * - Sidebar: サイドバーコンポーネント（予約詳細表示用）
 */
import Sidebar from "primevue/sidebar";

/**
 * 日付・時刻操作ライブラリ
 * - moment: 日付・時刻の操作とフォーマット
 */
import moment from "moment";

/**
 * 予約関連のVueコンポーネント
 * - HeaderShowTemplate: 予約表示時のヘッダー
 * - BodyShow: 予約表示時のボディ
 * - HeaderEditTemplate: 予約編集時のヘッダー
 * - BodyEdit: 予約編集時のボディ
 * - HeaderNewTemplate: 予約新規作成時のヘッダー
 * - BodyNew: 予約新規作成時のボディ
 * - HeaderDuplicateTemplate: 予約複製時のヘッダー
 * - BodyDuplicate: 予約複製時のボディ
 */
import HeaderShowTemplate from "./components/show/template/header.vue";
import BodyShow from "./components/show/body.vue";
import HeaderEditTemplate from "./components/edit/template/header.vue";
import BodyEdit from "./components/edit/body.vue";
import HeaderNewTemplate from "./components/new/template/header.vue";
import BodyNew from "./components/new/body.vue";
import HeaderDuplicateTemplate from "./components/duplicate/template/header.vue";
import BodyDuplicate from "./components/duplicate/body.vue";

/**
 * 予約関連の型定義
 * - APIリクエスト/レスポンスの型
 * - 予約データのインターフェース
 * - メニュー情報の型
 */
import {
  ApiBaseReqParams,
  ApiBookingSaveReq,
  ApiBookingShowReq,
  IBooking,
  IBookingsEventExtendProps,
  IMenu,
} from "@/clinic/typings";

/**
 * 予約関連のAPIサービス
 * - 予約の取得、作成、更新、削除などの操作
 */
import { BookingServices } from "@/clinic/services";

/**
 * Axios HTTPライブラリ
 * - AxiosResponse: HTTPレスポンスの型定義
 */
import { AxiosResponse } from "axios";

/**
 * 通知ユーティリティ
 * - notifySuccessMsg: 成功メッセージの表示
 * - notityErrorMsg: エラーメッセージの表示
 */
import { notifySuccessMsg, notityErrorMsg } from "@/clinic/utils/notify-utils";

/**
 * 予約関連の列挙型
 * - BookingActionEnum: 予約アクションの種類
 * - BookingViewPageTypeEnum: 予約表示ページの種類
 * - BookingViewTypeEnum: 予約表示タイプの種類
 * - BookingResourceId: 予約リソースIDの種類
 */
import {
  BookingActionEnum,
  BookingViewPageTypeEnum,
  BookingViewTypeEnum,
  BookingResourceId,
} from "@/clinic/typings/enums/booking";

/**
 * FullCalendar関連の型定義
 * - EventClickArg: イベントクリック時の引数
 * - EventInput: イベント入力データ
 */
import { EventClickArg, EventInput } from "@fullcalendar/core";

/**
 * イベントユーティリティ
 * - createEventId: イベントIDの生成
 */
import { createEventId } from "@/clinic/utils/event-utils";

/**
 * FullCalendar内部の型定義
 * - Dictionary: 辞書型
 * - EventImpl: イベント実装クラス
 */
import { Dictionary, EventImpl } from "@fullcalendar/core/internal";

/**
 * 時間操作ユーティリティ
 * - 日付・時刻の変換と操作
 */
import TimeUtils from "@/clinic/utils/time-utils";

/**
 * PrimeVue Toastサービス
 * - ToastServiceMethods: トースト通知のメソッド
 */
import { ToastServiceMethods } from "primevue/toastservice";

/**
 * カレンダーユーティリティ
 * - カレンダー表示タイプの取得
 */
import CalendarUtils from "@/clinic/utils/calendar-utils";

/**
 * Vue Router
 * - useRoute: 現在のルート情報の取得
 */
import { useRoute } from "vue-router";

/**
 * 予約関連の定数
 * - DATETIME_ISO_FORMAT: 日時フォーマットの定数
 */
import { DATETIME_ISO_FORMAT } from "@/clinic/typings/constants/booking";

/**
 * アプリケーション定数
 * マジックナンバーを定数として定義
 */
type ISidebar = {
  /** トースト通知サービス */
  toast: ToastServiceMethods;
  /** 表示タイプ */
  viewType: string;
  /** 予約履歴一覧の再読み込み関数 */
  onReloadListBookingHistory: Fn;
  /** 残りスロットの再取得関数 */
  reFetchRemainningSlot: Fn;
};

/**
 * コンポーネントのプロパティ定義
 * デフォルト値を設定
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'booking-sidebar',
  props: {
    toast: null,
    viewType: { default: BookingViewTypeEnum.Day },
    onReloadListBookingHistory: null,
    reFetchRemainningSlot: null
  },
  setup(__props: any, { expose }) {

const props = __props as { toast: ToastServiceMethods, viewType: string, onReloadListBookingHistory: Fn, reFetchRemainningSlot: Fn };

/**
 * Vue 3 Composition APIの基本機能
 * - onBeforeMount: コンポーネントマウント前の処理
 * - ref: リアクティブな参照を作成
 * - inject: 親コンポーネントから値を注入
 * - shallowRef: 浅いリアクティブ参照を作成
 * - withDefaults: プロパティのデフォルト値を設定
 * - defineProps: コンポーネントのプロパティを定義
 * - computed: 計算されたプロパティを作成
 */
const APP_CONSTANTS = {
  /** デフォルトオプション時間（分） */
  DEFAULT_OPTION_TIME_UNIT: 10,
  /** 最小オプション時間（分） */
  MIN_OPTION_TIME_UNIT: 0,
  /** デフォルト予約単位（分） */
  DEFAULT_BOOKING_DURATION: 30,
  /** 複製フォーム初期化遅延（ミリ秒） */
  DUPLICATE_FORM_DELAY: 100,
} as const;

/** サイドバーの表示状態 */
const visible = ref(false);

/**
 * 予約アクション別のコンポーネント定義
 * - NewBooking: 新規予約作成時のコンポーネント
 * - ShowBooking: 予約表示時のコンポーネント
 * - EditBooking: 予約編集時のコンポーネント
 * - DuplicateBooking: 予約複製時のコンポーネント
 */
const components = {
  NewBooking: [HeaderNewTemplate, BodyNew],
  ShowBooking: [HeaderShowTemplate, BodyShow],
  EditBooking: [HeaderEditTemplate, BodyEdit],
  DuplicateBooking: [HeaderDuplicateTemplate, BodyDuplicate],
};

/** 現在の予約アクション */
const action = ref("");

/** イベント引数（クリックまたは入力） */
const eventArg = ref<EventClickArg | EventInput>();

/** 現在の予約データ */
const booking = ref<IBooking>();

/** 元の予約データ（複製モード用） */
const originalBooking = ref<IBooking>();

/** 予約リクエストデータ */
const bookingReq = ref<IBooking>();

/** クリニックID */
const clinicId = ref(0);

/** ヘッダーコンポーネント */
const headerCom = shallowRef("");

/** ボディコンポーネント */
const bodyCom = shallowRef("");

/** 症状メモ */
const note_symptom = ref("");

/** 予約タイプ */
const bookingType = ref("");

/** 繰り返しパラメータの変更フラグ */
const changeRepeatParam = ref(false);

/** 元の予約タイプ */
const originalBookingType = ref("");

/** 現在のルート情報 */
const route = useRoute();

/** Vuexストア */
const store = useStore();

/**
 * 統一されたエラーハンドリング関数
 * @param {any} error - エラーオブジェクト
 * @param {string} defaultMessage - デフォルトエラーメッセージ
 * @returns {void}
 */
const handleApiError = (error: any, defaultMessage: string): void => {
  const errorMessage = error.response?.data?.errors || error.response?.data?.error || defaultMessage;
  notityErrorMsg(get(props.toast), errorMessage);
};

/**
 * 予約リソースIDを計算する
 * @returns {string} カレンダータイプに応じたリソースID
 */
const bookingResourceId = computed(() => {
  const calendarType = route.query.view_page as BookingViewPageTypeEnum;
  if (calendarType === BookingViewPageTypeEnum.StaffCalendar) return BookingResourceId.Staff;
  if (calendarType === BookingViewPageTypeEnum.RoomCalendar) return BookingResourceId.Room;
  return "";
});

/**
 * サイドバーコンポーネントのプロパティ型定義
 */


/**
 * 予約リクエストデータを設定する
 * @param {IBooking} bookingX - 設定する予約データ
 */
const setBookingReq = (bookingX: IBooking) => set(bookingReq, bookingX);

/**
 * アクションを設定する
 * @param {string} actionX - 設定するアクション
 */
const setAction = (actionX: string) => set(action, actionX);

/**
 * イベント引数を設定する
 * @param {EventClickArg | EventInput} eventArgX - 設定するイベント引数
 */
const setEventArg = (eventArgX: EventClickArg | EventInput) => set(eventArg, eventArgX);

/**
 * 予約リクエストデータをロードする
 * 基本的な予約情報を設定
 */
const loadBookingReq = () => {
  const { start_time, end_time, staff_id, note, room_id, is_free_space } = get(bookingReq) as IBooking;
  set(booking, { start_time, end_time, staff_id, note, room_id, is_free_space } as IBooking);
};

/**
 * 現在のビュータイプを計算する
 * @returns {string} カレンダーのビュータイプ
 */
const viewType = computed((): string => CalendarUtils.getViewTypeByViewImpl(get(eventArg)?.view?.type.toString()));

/**
 * 予約表示・編集・新規作成・複製のサイドバーを表示する
 * @param {IBooking} bookingX - 予約データ
 * @param {BookingActionEnum} action - 実行するアクション
 * @param {EventClickArg | EventInput} eventX - イベント情報
 */
const onShow = async (bookingX: IBooking, action: BookingActionEnum, eventX: EventClickArg | EventInput) => {
  const { event } = eventX;
  setEventArg(eventX);
  setBookingReq(bookingX);

  if (!event?.startEditable) event?.setProp("startEditable", true);

  const loadBookingActions = [BookingActionEnum.show, BookingActionEnum.edit, BookingActionEnum.duplicate];
  if (action == BookingActionEnum.new) loadBookingReq();
  if (loadBookingActions.includes(action)) await loadBooking(bookingX?.id as number);

  // 複製の場合は、元の予約データをコピーして新規作成用に準備
  if (action === BookingActionEnum.duplicate) {
    const currentBooking = get(booking);
    if (currentBooking) {
      // 元の予約データを保存（キャンセル時に復元するため）
      set(originalBooking, { ...currentBooking });

      // 元の予約データをコピーして、IDを削除して新規作成用に準備
      const duplicateBooking = { ...currentBooking };
      if ("id" in duplicateBooking) {
        delete (duplicateBooking as any).id;
      }

      // 複製時は予約タイプをnormalに設定
      duplicateBooking.booking_type = "normal";

      // 顧客情報を確実にコピー
      if (currentBooking.customer_id) {
        duplicateBooking.customer_id = currentBooking.customer_id;
      }
      if (currentBooking.customer_name) {
        duplicateBooking.customer_name = currentBooking.customer_name;
      }
      if (currentBooking.customer) {
        duplicateBooking.customer = currentBooking.customer;
      }

      // 予約時間、メニュー情報、施術終了時間を確実にコピー
      if (currentBooking.start_time) {
        duplicateBooking.start_time = currentBooking.start_time;
        // FormBookingコンポーネントが期待する形式で時間を設定
        const startTime = new Date(currentBooking.start_time);
        const startTimeStr = startTime.toLocaleTimeString("ja-JP", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        (duplicateBooking as any).startTime = startTimeStr;
        (duplicateBooking as any).startTimeValue = startTimeStr;
      }
      if (currentBooking.end_time) {
        duplicateBooking.end_time = currentBooking.end_time;
        // FormBookingコンポーネントが期待する形式で時間を設定
        const endTime = new Date(currentBooking.end_time);
        const endTimeStr = endTime.toLocaleTimeString("ja-JP", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        (duplicateBooking as any).endTime = endTimeStr;
        (duplicateBooking as any).endTimeValue = endTimeStr;
      }
      if (currentBooking.medical_menu_id) {
        duplicateBooking.medical_menu_id = currentBooking.medical_menu_id;
      }
      if (currentBooking.medical_menu) {
        duplicateBooking.medical_menu = currentBooking.medical_menu;
      }
      if (currentBooking.medical_menu_category_id) {
        duplicateBooking.medical_menu_category_id = currentBooking.medical_menu_category_id;
      }
      // 複製時は元の予約のオプション情報をコピー（フォームの状態は後で反映される）
      if (currentBooking.medical_option_ids) {
        duplicateBooking.medical_option_ids = [...currentBooking.medical_option_ids];
      }
      if (currentBooking.medical_option_names) {
        duplicateBooking.medical_option_names = [...currentBooking.medical_option_names];
      }
      // オプションメニューオブジェクトもコピー
      if ((currentBooking as any).medical_options) {
        (duplicateBooking as any).medical_options = [...(currentBooking as any).medical_options];
      }
      if (currentBooking.staff_id) {
        duplicateBooking.staff_id = currentBooking.staff_id;
      }
      if (currentBooking.staff) {
        duplicateBooking.staff = currentBooking.staff;
      }
      if (currentBooking.room_id) {
        duplicateBooking.room_id = currentBooking.room_id;
      }
      if (currentBooking.room) {
        duplicateBooking.room = currentBooking.room;
      }

      set(booking, duplicateBooking);
      set(originalBookingType, currentBooking.booking_type);
      set(bookingType, "normal");
    }
  }

  setAction(action);
  set(visible, true);
  applyAction();

  // 複製の場合は、フォームの初期化を少し遅らせる
  if (action === BookingActionEnum.duplicate) {
    await new Promise(resolve => setTimeout(resolve, APP_CONSTANTS.DUPLICATE_FORM_DELAY));
  }
};

/**
 * サイドバーを閉じる
 * 必要に応じて予約履歴一覧を再読み込み
 */
const onClose = () => {
  if (route.query.view_page === BookingViewPageTypeEnum.List) {
    props.onReloadListBookingHistory();
  }
  set(visible, false);
  set(changeRepeatParam, false);
  set(originalBookingType, "");
  set(bookingType, "");
  const event = get(eventArg)?.event as EventImpl;
  event?.setProp("startEditable", undefined);
};

/** ボディ要素の参照 */
const bodyElm = ref<any>(null);

/**
 * 日表示用のイベントプロパティを設定する
 * @param {IBooking} booking - 予約データ
 * @param {IBookingsEventExtendProps} extendProps - 拡張プロパティ
 */
const setEventDayProps = (
  { start_time, end_time, is_free_space }: IBooking,
  extendProps: IBookingsEventExtendProps
) => {
  const event = get(eventArg)?.event as EventImpl;
  if (!event) return;

  event.setProp("startEditable", true);

  // 予約リソースIDを取得
  const resourceKey = get(bookingResourceId);
  let resourceId = resourceKey ? `${resourceKey}` : "";
  if (is_free_space) {
    resourceId = `${resourceId}fs`;
  }

  event.setResources([resourceId]);
  event.setStart(TimeUtils.toLocalTime(start_time.toString()));
  event.setEnd(TimeUtils.toLocalTime(end_time.toString()));
  Object.entries(extendProps).forEach(([field, val]) => event.setExtendedProp(field, val));
};

/**
 * 週表示用のイベントプロパティを設定する
 * @param {IBooking} booking - 予約データ
 * @param {IBookingsEventExtendProps} extendProps - 拡張プロパティ
 * @param {EventImpl | null} oldEvent - 既存のイベント（オプション）
 */
const setEventWeekProps = (
  booking: IBooking,
  extendProps: IBookingsEventExtendProps,
  oldEvent: EventImpl | null = null
) => {
  const event = (get(eventArg)?.event || oldEvent) as EventImpl;
  if (!event) return;

  event.setProp("startEditable", true);

  let resourceId = `${booking[get(bookingResourceId) as keyof IBooking]}`;
  // 自由予約の場合はresourceIdにfsを追加
  if (booking.is_free_space) {
    resourceId = `${resourceId}fs`;
  }

  event.setResources([resourceId]);
  const { bookingsCount, bookings } = buildWeekExtProps(event, booking);
  Object.entries({ ...extendProps, bookingsCount, bookings }).forEach(([field, val]) =>
    event.setExtendedProp(field, val)
  );
};

/**
 * 新しいイベントを作成する
 * @param {IBooking} booking - 予約データ
 * @param {IBookingsEventExtendProps} extendProps - 拡張プロパティ
 */
const createEvent = ({ id, start_time, end_time, is_free_space }: IBooking, extendProps: IBookingsEventExtendProps) => {
  const eventArgData = get(eventArg);

  if (!eventArgData || !("resource" in eventArgData) || !eventArgData.resource || !eventArgData.resource._context) {
    console.warn("createEvent: eventArg, resource, or _context is undefined");
    return;
  }

  const { startStr: start, endStr: end, resource, title } = eventArgData as EventInput;
  const api = resource._context.calendarApi;
  // 予約リソースIDを取得、またはリソースのIDをフォールバックとして使用
  const resourceKey = get(bookingResourceId);
  let resourceId;

  if (resourceKey && resourceKey !== "staff_id") {
    // bookingResourceIdが有効な値の場合
    resourceId = `${resourceKey}`;
  } else {
    // フォールバック: スタッフIDを使用
    resourceId = `${extendProps.staffId || 1}`;
  }

  // 自由予約の場合はresourceIdにfsを追加
  if (is_free_space) {
    resourceId = `${resourceId}fs`;
  }

  const input = {
    id: id || createEventId(),
    title,
    start: TimeUtils.toLocalTime(start_time.toString()) || start,
    end: TimeUtils.toLocalTime(end_time.toString()) || end,
    allDay: get(viewType) == BookingViewTypeEnum.Week,
    resourceId,
    resource: { id: parseInt(resourceId) },
    startEditable: true,
    extendedProps: extendProps as Dictionary,
  } as EventInput;
  api?.unselect();
  api?.addEvent(input);
};

/**
 * 週表示用の拡張プロパティを構築する
 * @param {EventImpl} event - イベント
 * @param {IBooking} booking - 予約データ
 * @returns {Object} 拡張プロパティ
 */
const buildWeekExtProps = (event: EventImpl, booking: IBooking) => {
  // 個別予約表示のため、常に単一の予約として扱う
  return { bookingsCount: 1, bookings: [booking] };
};

/**
 * 週IDで予約済みイベントを取得する
 * @param {string} weekId - 週ID
 * @returns {EventImpl} イベント
 */
const bookedEventById = (weekId: string): EventImpl => {
  const { resource } = get(eventArg) as EventInput;
  const api = resource._context.calendarApi;
  return api.getEventById(weekId) as EventImpl;
};

/**
 * 週表示での新規イベントアクション
 * @param {IMenu | undefined} medical_menu - 医療メニュー
 * @param {Date} start_time - 開始時刻
 * @param {IBookingsEventExtendProps} evtProps - イベントプロパティ
 * @param {IBooking} booking - 予約データ
 */
const onNewEventActionWeek = (
  medical_menu: IMenu | undefined,
  start_time: Date,
  evtProps: IBookingsEventExtendProps,
  booking: IBooking
) => {
  const weekId = `${booking[get(bookingResourceId) as keyof IBooking]}_${medical_menu?.id}_${moment(start_time).format(
    "DDMMYYYY"
  )}_${booking.id}`;
  const oldEvent = bookedEventById(weekId);
  const extendedProps = {
    ...evtProps,
    ...buildWeekExtProps(oldEvent, booking),
    weekId,
  };
  oldEvent
    ? setEventWeekProps(booking, extendedProps, oldEvent)
    : createEvent({ ...booking, id: weekId }, extendedProps);
};

/**
 * 日表示での新規イベントアクション
 * @param {IBookingsEventExtendProps} evtProps - イベントプロパティ
 * @param {IBooking} booking - 予約データ
 */
const onNewEventActionDay = (evtProps: IBookingsEventExtendProps, booking: IBooking) => createEvent(booking, evtProps);

/**
 * 予約保存成功時の処理
 * @param {IBooking} booking - 保存された予約データ
 */
const onSuccessSaving = (booking: IBooking) => {
  const evtProps = {
    reservationId: booking.id,
    customerId: booking.customer_id,
    customerName: booking.customer_name,
    customerStatus: booking.customer_status,
    customerStatusDisplayName: booking.customer_status_display_name,
    medicalMenuCategoryId: booking.medical_menu_category_id,
    medicalMenuId: booking.medical_menu?.id,
    medicalMenuName: booking.medical_menu?.name,
    medicalOptionIds: booking.medical_option_ids,
    staff: booking.staff,
    note: booking.note,
    note_symptom: booking.note_symptom,
    specialNotes: booking.special_notes,
    status: booking.status,
    bookingType: booking.booking_type,
    additionalFees: booking.additional_fees,
    bookingTotalAmount: booking.total_amount,
    staffId: booking.staff_id,
    roomId: booking.room_id,
    room: booking.room,
  } as unknown as IBookingsEventExtendProps;
  switch (get(action)) {
    case BookingActionEnum.new:
      get(viewType) == BookingViewTypeEnum.Week
        ? onNewEventActionWeek(booking.medical_menu, booking.start_time, evtProps, booking)
        : onNewEventActionDay(evtProps, booking);
      break;
    case BookingActionEnum.edit:
      get(viewType) == BookingViewTypeEnum.Day ? setEventDayProps(booking, evtProps) : refetchEvents();
      break;
  }
  notifySuccessMsg(get(props.toast), "保存成功");
  onClose();
  props.reFetchRemainningSlot();
};

/**
 * バッチ予約保存成功時の処理
 * @param {any} bookings - 保存された予約データ
 */
const onSuccessBatchSaving = (bookings: any) => {
  const booking = bookings["bookings"][0] as IBooking;
  const evtProps = {
    reservationId: booking.id,
    customerId: booking.customer_id,
    customerName: booking.customer_name,
    customerStatus: booking.customer_status,
    customerStatusDisplayName: booking.customer_status_display_name,
    medicalMenuCategoryId: booking.medical_menu_category_id,
    medicalMenuId: booking.medical_menu?.id,
    medicalMenuName: booking.medical_menu?.name,
    medicalOptionIds: booking.medical_option_ids,
    staff: booking.staff,
    note: booking.note,
    note_symptom: booking.note_symptom,
    specialNotes: booking.special_notes,
    status: booking.status,
    bookingType: booking.booking_type,
    additionalFees: booking.additional_fees,
    bookingTotalAmount: booking.total_amount,
    staffId: booking.staff_id,
    roomId: booking.room_id,
    room: booking.room,
  } as unknown as IBookingsEventExtendProps;
  switch (get(action)) {
    case BookingActionEnum.new:
      get(viewType) == BookingViewTypeEnum.Week
        ? onNewEventActionWeek(booking.medical_menu, booking.start_time, evtProps, booking)
        : onNewEventActionDay(evtProps, booking);
      break;
    case BookingActionEnum.edit:
      get(viewType) == BookingViewTypeEnum.Day ? setEventDayProps(booking, evtProps) : refetchEvents();
      break;
  }
  notifySuccessMsg(get(props.toast), "保存成功");
  onClose();
  props.reFetchRemainningSlot();
};

/**
 * 複製予約用のイベントを作成する
 * @param {IBooking} booking - 複製された予約データ
 * @param {IBookingsEventExtendProps} extendProps - イベントプロパティ
 */
const createDuplicateEvent = (booking: IBooking, extendProps: IBookingsEventExtendProps) => {
  const event = get(eventArg)?.event as EventImpl;
  if (!event) {
    console.warn("createDuplicateEvent: event is not available");
    return;
  }

  const api = event._context?.calendarApi;
  if (!api) {
    console.warn("createDuplicateEvent: calendarApi is not available");
    return;
  }

  // 複製予約用のリソースIDを設定
  let resourceId = `${booking.staff_id}`;
  if (booking.is_free_space) {
    resourceId = `${resourceId}fs`;
  }

  const input = {
    id: booking.id || createEventId(),
    title: booking.customer_name || "複製予約",
    start: TimeUtils.toLocalTime(booking.start_time.toString()),
    end: TimeUtils.toLocalTime(booking.end_time.toString()),
    allDay: get(viewType) == BookingViewTypeEnum.Week,
    resourceId,
    resource: { id: parseInt(resourceId) },
    startEditable: true,
    extendedProps: extendProps as Dictionary,
  } as EventInput;

  api.unselect();
  api.addEvent(input);
};

/**
 * イベントを再取得する
 * カレンダーからすべてのイベントを削除して再読み込み
 */
const refetchEvents = () => {
  const event = get(eventArg)?.event as EventImpl;
  event._context.calendarApi.removeAllEvents();
  event._context.calendarApi.refetchEvents();
};

/**
 * 予約タイプを更新する
 * @param {string} type - 更新する予約タイプ
 */
const onUpdate = (type: string) => {
  set(bookingType, type);
};

/**
 * 繰り返しパラメータの変更を処理する
 */
const onChangeRepeatParam = () => {
  set(changeRepeatParam, true);
};

/**
 * 予約を保存する
 * @param {string} updateType - 更新タイプ（今回のみ / 今回と次回以降）
 */
const onSave = async (updateType: string) => {
  const hasValidate = await get(bodyElm)?.validate();
  if (!hasValidate) return;

  const form = get(bodyElm)?.getForm();
  const formData = new FormData(get(form) as unknown as HTMLFormElement);
  const params = { id: get(booking)?.id, clinic_id: get(get(clinicId)) as number } as unknown as ApiBookingShowReq;
  if (formData.get("booking_type") == "repeat") {
    const repeatData = formData.get("repeat_data");
    if (formData.get("repeat_rule") == "1") {
      repeatData
        ?.toString()
        .split(",")
        .forEach((val: string) => {
          formData.append(`repeat_data[]`, val);
        });
    }
    const repeatEndData = formData.get("repeat_ends");
    formData.delete("repeat_ends");
    if (formData.get("repeat-end-type") == "day") {
      const formattedDate = moment(repeatEndData?.toString()).format("YYYY-MM-DD");
      formData.append("repeat_ends", JSON.stringify({ on: formattedDate }));
    } else {
      formData.append("repeat_ends", JSON.stringify({ after: repeatEndData }));
    }

    if (updateType == "update_only_this") {
      await BookingServices.saveBooking(params, formData as unknown as ApiBookingSaveReq, onSuccessSaving, errors =>
        handleApiError(errors, "予約の保存に失敗しました")
      );
    } else {
      await BookingServices.saveBatchBooking(
        params,
        formData as unknown as ApiBookingSaveReq,
        onSuccessBatchSaving,
        errors => handleApiError(errors, "バッチ予約の保存に失敗しました")
      );
    }
  } else {
    formData.delete("repeat-status");
    if (route.query.view_page === BookingViewPageTypeEnum.List) {
      await BookingServices.saveBooking(
        params,
        formData as unknown as ApiBookingSaveReq,
        () => {
          notifySuccessMsg(get(props.toast), "保存成功");
          props.reFetchRemainningSlot();
        },
        errors => handleApiError(errors, "予約の保存に失敗しました")
      );
    } else {
      if (bookingType.value == "repeat" && originalBookingType.value == "normal") {
        await BookingServices.saveBatchBooking(
          params,
          formData as unknown as ApiBookingSaveReq,
          onSuccessBatchSaving,
          errors => handleApiError(errors, "バッチ予約の保存に失敗しました")
        );
      } else {
        await BookingServices.saveBooking(params, formData as unknown as ApiBookingSaveReq, onSuccessSaving, errors => {
          handleApiError(errors, "予約の保存に失敗しました");
        });
      }
    }
  }
};

/**
 * 日時をフォーマットする
 * @param {Date} date - フォーマットする日時
 * @returns {string} フォーマットされた日時文字列
 */
const formatDateTime = (date: Date): string => {
  return get(useDateFormat(date, DATETIME_ISO_FORMAT));
};

/**
 * 複製時の基本フォームデータを準備する
 * 繰り返し設定を削除し、基本的な予約情報を設定
 * @param {FormData} formData - フォームデータ
 * @param {IBooking} booking - 元の予約データ
 * @returns {FormData} 基本設定済みのフォームデータ
 */
const prepareBasicDuplicateFormData = (formData: FormData, booking: IBooking): FormData => {
  // 複製時は繰り返し設定を削除して通常の予約として作成
  formData.delete("repeat-status");
  formData.delete("repeat_data");
  formData.delete("repeat_ends");
  formData.delete("repeat-end-type");
  formData.delete("repeat_rule");

  // booking_typeをnormalに設定
  formData.set("booking_type", "normal");

  // 複製時は元の予約のis_free_spaceの値をそのまま使用
  const originalIsFreeSpace = booking?.is_free_space;
  if (originalIsFreeSpace !== undefined) {
    formData.set("is_free_space", originalIsFreeSpace.toString());
  }

  // 複製時はスタッフ情報が確実に含まれるようにする
  if (booking?.staff_id) {
    formData.set("staff_id", booking.staff_id.toString());
  }

  // 複製時はフォームの現在の状態をそのまま使用
  // FormDataは既にフォームの現在の状態を含んでいるため、オプション情報は変更しない

  return formData;
};

/**
 * フォームコンポーネントからオプション情報を取得する
 * @param {any} formComponent - フォームコンポーネント
 * @returns {any[] | null} オプション情報の配列
 */
const getMedicalOptionsFromForm = (formComponent: any): any[] | null => {
  if (!formComponent) return null;

  // 1. 公開メソッドから取得を試行
  if (typeof formComponent.getMedicalOptions === "function") {
    return formComponent.getMedicalOptions();
  }

  // 2. Vue 3の内部構造を探索
  const internalData = formComponent.setupState || formComponent.ctx || formComponent.exposed;
  if (internalData && internalData.medicalOptions) {
    return internalData.medicalOptions;
  }

  // 3. フォームオブジェクトに直接アクセス
  if (formComponent.getForm) {
    try {
      const formObj = formComponent.form || formComponent._form || formComponent.$form;
      if (formObj && formObj.medicalOptions) {
        return formObj.medicalOptions;
      }
    } catch (e) {
      // エラーは無視
    }
  }

  return null;
};

/**
 * DOM要素からオプション情報を取得する
 * @param {any} formComponent - フォームコンポーネント
 * @returns {any[] | null} オプション情報の配列
 */
const getMedicalOptionsFromDOM = (formComponent: any): any[] | null => {
  if (!formComponent || !formComponent.getForm) return null;

  try {
    const formElement = formComponent.getForm();
    const optionElements = formElement.querySelectorAll('input[name="medical_option_ids[]"]:checked');

    if (optionElements.length === 0) return null;

    return Array.from(optionElements).map((el: any) => {
      const optionId = el.value;

      // 様々なパターンで時間要素を検索
      let timeElement =
        formElement.querySelector(`input[name="medical_option_time_${optionId}"]`) ||
        formElement.querySelector(`input[name="option_time_${optionId}"]`) ||
        formElement.querySelector(`input[name="time_unit_${optionId}"]`) ||
        formElement.querySelector(`input[data-option-id="${optionId}"][name*="time"]`);

      // 親要素内で時間要素を検索
      if (!timeElement) {
        const parentElement = (el as any).closest(".field-checkbox, .p-checkbox, .field, .formgrid, .p-field");
        if (parentElement) {
          timeElement = parentElement.querySelector(
            'input[name*="time"], input[type="hidden"], .time-unit, .option-time'
          );
        }
      }

      const timeUnit = timeElement
        ? timeElement.value ||
          timeElement.textContent ||
          timeElement.getAttribute("data-time") ||
          APP_CONSTANTS.DEFAULT_OPTION_TIME_UNIT.toString()
        : APP_CONSTANTS.DEFAULT_OPTION_TIME_UNIT.toString();

      return { id: parseInt(optionId), time_unit: timeUnit };
    });
  } catch (e) {
    return null;
  }
};

/**
 * APIからオプション情報を取得する
 * @param {number[]} optionIds - オプションID配列
 * @returns {Promise<number>} オプションの合計時間（分）
 */
const getMedicalOptionsFromAPI = async (optionIds: number[]): Promise<number> => {
  try {
    const { MedicalOptionServices } = await import("@/clinic/services");
    const clinicIdValue = get(get(clinicId)) as number;

    const response = await MedicalOptionServices.getOptions(
      { clinic_id: clinicIdValue },
      () => {
        // 成功時の処理（特に何もしない）
      },
      () => {
        // エラーは無視
      }
    );

    const responseData = response.data?.value || response.data;
    if (!responseData || (!(responseData as any).medical_options && !(responseData as any).options)) {
      return APP_CONSTANTS.MIN_OPTION_TIME_UNIT;
    }

    const allOptions = (responseData as any).medical_options || (responseData as any).options;
    const selectedOptions = allOptions.filter((option: any) => optionIds.includes(option.id));

    if (selectedOptions.length === 0) return APP_CONSTANTS.MIN_OPTION_TIME_UNIT;

    const totalTime = selectedOptions.reduce((acc: number, option: any) => acc + (parseInt(option.time_unit) || 0), 0);

    return totalTime;
  } catch (e) {
    return APP_CONSTANTS.MIN_OPTION_TIME_UNIT;
  }
};

/**
 * オプションの合計時間を計算する
 * @param {string[]} selectedOptionIds - 選択されたオプションID配列
 * @param {IBooking} booking - 予約データ
 * @returns {Promise<number>} オプションの合計時間（分）
 */
const calculateOptionTimeUnit = async (selectedOptionIds: string[], booking: IBooking): Promise<number> => {
  if (!selectedOptionIds || selectedOptionIds.length === 0) return 0;

  const optionIds = selectedOptionIds.map(id => parseInt(id.toString())).filter(id => !isNaN(id));
  if (optionIds.length === 0) return 0;

  // フォームコンポーネントからオプション情報を取得
  const formComponent = get(bodyElm);
  let medicalOptions = getMedicalOptionsFromForm(formComponent);

  // DOM要素から取得を試行
  if (!medicalOptions) {
    medicalOptions = getMedicalOptionsFromDOM(formComponent);
  }

  let optionTimeUnit = 0;

  if (medicalOptions && Array.isArray(medicalOptions) && medicalOptions.length > 0) {
    const selectedOptions = medicalOptions.filter(option => optionIds.includes(option.id));
    optionTimeUnit = selectedOptions.reduce((acc, option) => acc + (parseInt(option.time_unit) || 0), 0);

    // オプションが見つからない場合や時間が0の場合、または時間がデフォルト値の場合はAPI呼び出しを試行
    if (
      selectedOptions.length === 0 ||
      optionTimeUnit === APP_CONSTANTS.MIN_OPTION_TIME_UNIT ||
      optionTimeUnit === APP_CONSTANTS.DEFAULT_OPTION_TIME_UNIT
    ) {
      const apiOptionTimeUnit = await getMedicalOptionsFromAPI(optionIds);
      if (apiOptionTimeUnit > APP_CONSTANTS.DEFAULT_OPTION_TIME_UNIT) {
        optionTimeUnit = apiOptionTimeUnit;
      }
    }
  } else {
    // 最後の手段: APIから直接取得
    optionTimeUnit = await getMedicalOptionsFromAPI(optionIds);

    // それでも取得できない場合はフォールバック
    if (optionTimeUnit === APP_CONSTANTS.MIN_OPTION_TIME_UNIT) {
      optionTimeUnit = optionIds.length * APP_CONSTANTS.DEFAULT_OPTION_TIME_UNIT;
    }
  }

  return optionTimeUnit;
};

/**
 * 時間変更に基づいてフォームデータを調整する
 * @param {FormData} formData - フォームデータ
 * @param {IBooking} booking - 元の予約データ
 * @returns {Promise<FormData>} 調整済みのフォームデータ
 */
const adjustFormDataForTimeChanges = async (formData: FormData, booking: IBooking): Promise<FormData> => {
  const formStartTime = formData.get("start_time");
  const formEndTime = formData.get("end_time");
  const originalStartTime = booking?.start_time;
  const originalEndTime = booking?.end_time;

  const isStartTimeChanged =
    formStartTime &&
    originalStartTime &&
    new Date(formStartTime.toString()).getTime() !== new Date(originalStartTime).getTime();
  const isEndTimeChanged =
    formEndTime &&
    originalEndTime &&
    new Date(formEndTime.toString()).getTime() !== new Date(originalEndTime).getTime();

  // 開始時間が変更された場合、メニューとオプションの合計時間に基づいて終了時間を自動計算
  if (isStartTimeChanged && booking?.medical_menu && booking.medical_menu.time_unit) {
    const startDate = new Date(formStartTime.toString());
    const menuTimeUnit = parseInt(booking.medical_menu.time_unit) || 0;
    const selectedOptionIds = formData.getAll("medical_option_ids[]") as string[];
    const optionTimeUnit = await calculateOptionTimeUnit(selectedOptionIds, booking);

    // メニューとオプションの合計時間
    const totalTime = menuTimeUnit + optionTimeUnit;
    const bookingDuration = store.getters["bookingSetting/bookingDuration"] || APP_CONSTANTS.DEFAULT_BOOKING_DURATION;
    const finalDuration =
      totalTime === APP_CONSTANTS.MIN_OPTION_TIME_UNIT
        ? bookingDuration
        : Math.ceil(totalTime / bookingDuration) * bookingDuration;

    // 開始時間に合計時間を加算して終了時間を計算
    const endDate = new Date(startDate.getTime() + finalDuration * 60 * 1000);

    formData.set("start_time", formatDateTime(startDate));
    formData.set("end_time", formatDateTime(endDate));
  } else if (isEndTimeChanged && !isStartTimeChanged) {
    // 終了時間のみが変更された場合、開始時間はそのまま、終了時間のみを更新
    const endDate = new Date(formEndTime.toString());
    formData.set("end_time", formatDateTime(endDate));
  } else if (!formStartTime || !formEndTime) {
    // フォームに時間が設定されていない場合は、元の時間を使用
    if (originalStartTime && originalEndTime) {
      const startDate = new Date(originalStartTime);
      const endDate = new Date(originalEndTime);

      formData.set("start_time", formatDateTime(startDate));
      formData.set("end_time", formatDateTime(endDate));
    }
  }

  return formData;
};

/**
 * フォームデータの準備処理を分離
 * 複製時は繰り返し設定を削除して通常の予約として作成
 * @param {FormData} formData - フォームデータ
 * @param {IBooking} booking - 元の予約データ
 * @returns {FormData} 準備されたフォームデータ
 */
const prepareDuplicateFormData = async (formData: FormData, booking: IBooking): Promise<FormData> => {
  // 1. 基本設定を適用
  const basicFormData = prepareBasicDuplicateFormData(formData, booking);

  // 2. 時間変更に基づく調整を適用
  const adjustedFormData = await adjustFormDataForTimeChanges(basicFormData, booking);

  return adjustedFormData;
};

/**
 * イベントプロパティの作成処理を分離
 * @param {IBooking} booking - 予約データ
 * @returns {IBookingsEventExtendProps} イベント拡張プロパティ
 */
const createEventProps = (booking: IBooking): IBookingsEventExtendProps => {
  return {
    reservationId: booking.id,
    customerId: booking.customer_id,
    customerName: booking.customer_name,
    customerStatus: booking.customer_status,
    medicalMenuCategoryId: booking.medical_menu_category_id,
    medicalMenuId: booking.medical_menu?.id || null,
    medicalMenuName: booking.medical_menu?.name || null,
    medicalOptionIds: booking.medical_option_ids,
    staff: booking.staff,
    note: booking.note,
    note_symptom: booking.note_symptom,
    specialNotes: booking.special_notes,
    status: booking.status,
    bookingType: booking.booking_type,
    additionalFees: booking.additional_fees,
    bookingTotalAmount: booking.total_amount,
    staffId: booking.staff_id,
    roomId: booking.room_id,
    room: booking.room,
  } as unknown as IBookingsEventExtendProps;
};

/**
 * 予約を複製する
 * 現在のフォームの状態で新しい予約を作成
 */
const onDuplicate = async () => {
  const bodyElmRef = get(bodyElm);
  if (!bodyElmRef) {
    return;
  }

  const hasValidate = await bodyElmRef.validate();
  if (!hasValidate) {
    return;
  }

  const form = bodyElmRef.getForm();
  const formData = new FormData(form as unknown as HTMLFormElement);

  // 複製時は元の予約IDを使用（元の予約を保護するため）
  const originalBookingId = get(bookingReq)?.id;
  const params = { id: originalBookingId, clinic_id: get(get(clinicId)) as number } as unknown as ApiBookingShowReq;

  // フォームデータの準備
  const currentBooking = get(booking);
  if (!currentBooking) {
    return;
  }
  const preparedFormData = await prepareDuplicateFormData(formData, currentBooking);

  await BookingServices.duplicateBooking(
    params,
    preparedFormData as unknown as ApiBookingSaveReq,
    (booking: IBooking) => {
      const evtProps = createEventProps(booking);

      // 複製時は専用の関数を使用してカレンダーにイベントを追加
      createDuplicateEvent(booking, evtProps);

      notifySuccessMsg(get(props.toast), "予約の複製が完了しました");
      onClose();
      props.reFetchRemainningSlot();
    },
    errors => {
      handleApiError(errors, "予約の複製に失敗しました");
    }
  );
};

/**
 * 複製をキャンセルする
 * サイドバーを閉じて元の状態に戻す
 */
const onCancel = () => {
  const currentAction = get(action);
  if (currentAction === BookingActionEnum.duplicate) {
    // 複製モードの場合は表示モードに戻す
    const savedOriginalBooking = get(originalBooking);

    // 元の予約データが存在する場合は復元
    if (savedOriginalBooking?.id) {
      set(booking, { ...savedOriginalBooking });
      setAction(BookingActionEnum.show);
      applyAction();
    } else {
      // 元の予約データがない場合はサイドバーを閉じる
      onClose();
    }
  } else {
    // その他の場合はサイドバーを閉じる
    onClose();
  }
};

/**
 * アクションに応じてコンポーネントを適用する
 * ヘッダーとボディのコンポーネントを設定
 */
const applyAction = () => {
  const [header, body] = components[get(action) as keyof typeof components];
  set(headerCom, header as any);
  set(bodyCom, body as any);
};

// eslint-disable-next-line no-undef
expose({
  onShow,
  onClose,
});

/**
 * クリニックIDパラメータを取得する
 * @returns {Promise<ApiBaseReqParams>} API基本リクエストパラメータ
 */
const clinicIdParams = async (): Promise<ApiBaseReqParams & { default_staff: string }> => ({
  clinic_id: get(await get(clinicId)),
  default_staff: "yes",
});

/**
 * 予約データをロードする
 * @param {number} id - ロードする予約のID
 */
const loadBooking = async (id: number) => {
  const { data: bookingData } = (await BookingServices.getBooking({
    id,
    ...(await clinicIdParams()),
  })) as unknown as AxiosResponse<IBooking>;

  let modifiedBookingData = { ...get(bookingData) };

  // 予約リクエストステータスが処理済みで、かつ予約タイプがrequestの場合はnormalに変更
  if (
    get(modifiedBookingData, "booking_request_status") !== "not_process" &&
    get(modifiedBookingData, "booking_type") === "request"
  ) {
    modifiedBookingData = { ...modifiedBookingData, booking_type: "normal" };
  } else if (get(modifiedBookingData, "booking_type") === "repeat") {
    // 繰り返し予約の場合はrepeatタイプを維持
    modifiedBookingData = { ...modifiedBookingData, booking_type: "repeat" };
  }

  // 自由予約の場合はroom_idの検証を不要にする
  if (get(modifiedBookingData, "is_free_space") === true) {
    // room_idまたはroomが不足している場合はデフォルト値を設定
    if (!get(modifiedBookingData, "room_id")) {
      modifiedBookingData = { ...modifiedBookingData, room_id: 0 };
    }
    if (!get(modifiedBookingData, "room")) {
      modifiedBookingData = {
        ...modifiedBookingData,
        room: { id: 0, name: "", capacity: 0, menus_count: 0, title: "" },
      };
    }
  }

  set(booking, modifiedBookingData);
  set(originalBookingType, bookingData?.booking_type);
  set(bookingType, bookingData?.booking_type);
  const noteSymptomValue = booking.value?.note_symptom;
  if (noteSymptomValue !== undefined) {
    note_symptom.value = noteSymptomValue;
  }
};

/**
 * コンポーネントマウント前の処理
 * クリニックIDを注入から取得
 */
onBeforeMount(async () => set(clinicId, await inject("clinicId")));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Sidebar), {
      visible: visible.value,
      "onUpdate:visible": _cache[0] || (_cache[0] = ($event: any) => ((visible).value = $event)),
      onHide: onClose,
      position: "right",
      class: "w-full md:w-20rem lg:w-30rem"
    }, {
      header: _withCtx(() => [
        (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(headerCom)), {
          eventArg: eventArg.value,
          bookingId: `${booking.value?.id}`,
          bookingType: bookingType.value,
          bookingStatus: booking.value?.status,
          bookingRequestStatus: booking.value?.booking_request_status,
          changeRepeatParam: changeRepeatParam.value,
          originalBookingType: originalBookingType.value,
          is_free_space: booking.value?.is_free_space,
          booking: booking.value,
          onSave: onSave,
          onDuplicate: onDuplicate,
          onCancel: onCancel
        }, null, 40, ["eventArg", "bookingId", "bookingType", "bookingStatus", "bookingRequestStatus", "changeRepeatParam", "originalBookingType", "is_free_space", "booking"]))
      ]),
      default: _withCtx(() => [
        (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(bodyCom)), {
          ref_key: "bodyElm",
          ref: bodyElm,
          eventArg: eventArg.value,
          booking: booking.value,
          clinicId: _unref(get)(clinicId.value),
          note_symptom: _unref(get)(note_symptom.value),
          toast: __props.toast,
          reFetchRemainningSlot: __props.reFetchRemainningSlot,
          onChangeRepeatParam: onChangeRepeatParam,
          onUpdate: onUpdate,
          onSave: onSave,
          onClose: onClose
        }, null, 40, ["eventArg", "booking", "clinicId", "note_symptom", "toast", "reFetchRemainningSlot"]))
      ]),
      _: 1
    }, 8, ["visible"])
  ]))
}
}

})